import { useContent } from "@/content/ContentProvider";
import React, { useState } from "react";
import { Check, Plus } from "lucide-react";
import { Reveal, Pill } from "./Reveal";
import { PillButton } from "./Kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
export function Method() {
  const { text: cmsText, METHOD } = useContent();
  return (
    <section id="method" className="px-5 pt-28 lg:pt-44" data-testid="method">
      <div className="mx-auto max-w-[1400px]">
        <Pill>{cmsText("HomeMid.Method.method.883069")}</Pill>
        <Reveal className="mt-5">
          <h2 className="nm-title max-w-[860px] text-[34px] leading-[1.03] sm:text-[56px]">
            {cmsText("HomeMid.Method.one-workflow-three-clear-steps-.f49c92")}
          </h2>
        </Reveal>
        <Tabs defaultValue="understand" className="mt-12">
          <TabsList className="h-auto flex-wrap gap-1 rounded-full bg-[#f7f7f7] p-1">
            {METHOD.map((m) => (
              <TabsTrigger
                key={m.key}
                value={m.key}
                data-testid={`method-tab-${m.key}`}
                className="rounded-full px-5 py-2 text-[13px] font-medium data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:shadow-none"
              >
                {m.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {METHOD.map((m) => (
            <TabsContent
              key={m.key}
              value={m.key}
              className="mt-4 duration-700 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-3"
            >
              <div className="rounded-[28px] bg-[#f7f7f7] p-6 sm:p-10">
                <h3 className="nm-title max-w-[720px] text-[26px] leading-[1.08] sm:text-[38px]">
                  {m.head}
                </h3>
                <div className="mt-10 grid gap-2 md:grid-cols-3">
                  {m.layers.map((l) => (
                    <div key={l.t} className="rounded-[20px] bg-white p-6">
                      <p className="text-[12px] opacity-40">{l.s}</p>
                      <p className="mt-1 text-[22px] tracking-[-0.02em]">
                        {l.t}
                      </p>
                      <ul className="mt-6 space-y-3 text-[14px] leading-[1.35]">
                        {l.items.map((it) => (
                          <li key={it} className="flex gap-2">
                            <Check size={14} className="mt-0.5 shrink-0" />
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="mt-2 rounded-[20px] bg-black p-6 text-white">
                  <span className="text-[12px] opacity-50">
                    {cmsText("HomeMid.Method.outcome.d3f061")}
                  </span>
                  <p className="mt-2 text-[18px] leading-[1.25] tracking-[-0.01em] sm:text-[22px]">
                    {m.outcome}
                  </p>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}

// Points on the step curve (viewBox 1000 x 120), one per step.
const STEP_PATH = "M20 100 C 400 100, 600 30, 980 22";
const STEP_MARKS = [
  [20, 100],
  [500, 64],
  [980, 22],
];

// The same three steps as Method, told in the journey's visual language:
// a large word, a card and a short list, with the step line as the selector.
export function MethodStory() {
  const { text: cmsText, METHOD, SERVICES } = useContent();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(0);
  const m = METHOD[step];
  const service = SERVICES[step];
  const word = service.title;
  const go = (i) => {
    setStep(i);
    setOpen(0);
  };
  const onKey = (e) => {
    if (e.key === "ArrowRight") go(Math.min(METHOD.length - 1, step + 1));
    if (e.key === "ArrowLeft") go(Math.max(0, step - 1));
  };
  return (
    <section
      id="method"
      className="scroll-mt-10 px-3 pt-28 sm:px-5 lg:pt-44"
      data-testid="method"
    >
      <div className="mx-auto max-w-[1400px] rounded-[28px] bg-[#f7f7f7] p-5 sm:p-10 lg:p-14">
        <div className="flex items-center justify-between">
          <Pill white>{cmsText("HomeMid.Method.method.883069")}</Pill>
          <span className="rounded-full bg-white px-4 py-1.5 text-[12px] font-medium tabular-nums">
            0{step + 1} / 0{METHOD.length}
          </span>
        </div>
        <h2 className="sr-only">
          {cmsText("HomeMid.Method.one-workflow-three-clear-steps-.f49c92")}
        </h2>

        <div
          id="method-panel"
          role="tabpanel"
          aria-labelledby={`method-tab-${m.key}`}
          className="mt-8 grid items-center gap-6 lg:mt-4 lg:min-h-[560px] lg:grid-cols-12 lg:gap-10"
        >
          <div className="nm-method-type min-w-0 lg:col-span-7">
            <p
              className="nm-display overflow-hidden whitespace-nowrap font-medium leading-[0.95] tracking-[-0.03em]"
              style={{ fontSize: `${Math.min(27, 148 / word.length)}cqw` }}
              aria-hidden="true"
            >
              <span className="nm-word-mask">
                <span key={m.key} className="nm-word">
                  {word}
                </span>
              </span>
            </p>
          </div>

          <div className="min-w-0 lg:col-span-5">
            <div
              key={m.key}
              className="animate-in fade-in slide-in-from-bottom-4 rounded-[24px] bg-white p-5 duration-700 sm:p-7"
            >
              <span className="inline-flex rounded-full bg-black px-3 py-1 text-[11px] font-medium text-white">
                {service.short}
              </span>
              <h3 className="nm-title mt-4 text-[28px] leading-[1.08] sm:text-[36px] xl:text-[40px]">
                {m.head}
              </h3>
              <p className="mt-4 text-[12px] opacity-45">
                {cmsText("HomeMid.Method.outcome.d3f061")}
              </p>
              <p className="mt-1 max-w-[480px] text-[14px] leading-[1.4] opacity-70 sm:text-[17px]">
                {m.outcome}
              </p>
              <div className="mt-5">
                <PillButton to={`/services#${m.key}`} size="lg">
                  {word}
                </PillButton>
              </div>
            </div>

            <div className="mt-4 rounded-[20px] bg-white p-4 sm:mt-6 sm:p-5">
              {m.layers.map((l, k) => {
                const isOpen = open === k;
                return (
                  <div
                    key={l.t}
                    className={k ? "mt-3 border-t border-black/5 pt-3" : ""}
                  >
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : k)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-4 text-left text-[12px]"
                    >
                      <span className="opacity-60">{l.t}</span>
                      <span className="flex items-center gap-2 font-medium">
                        {l.s}
                        <Plus
                          size={12}
                          className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                    <div
                      className={`grid transition-[grid-template-rows] duration-500 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                    >
                      <ul className="overflow-hidden text-[13px] leading-[1.4]">
                        {l.items.map((it, n) => (
                          <li
                            key={it}
                            className={`flex gap-2 opacity-75 ${n ? "mt-1.5" : "mt-3"}`}
                          >
                            <Check size={13} className="mt-0.5 shrink-0" />
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div
          role="tablist"
          aria-label={cmsText(
            "HomeMid.Method.one-workflow-three-clear-steps-.f49c92",
          )}
          onKeyDown={onKey}
          className="relative mt-8 h-[72px] w-full sm:h-[96px]"
        >
          <svg
            viewBox="0 0 1000 120"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full overflow-visible"
            aria-hidden="true"
          >
            <path
              d={STEP_PATH}
              fill="none"
              stroke="rgba(0,0,0,.16)"
              strokeWidth="1.5"
              strokeDasharray="2 6"
              vectorEffect="non-scaling-stroke"
            />
            <defs>
              <clipPath id="nm-method-clip">
                <rect
                  x="0"
                  y="-20"
                  height="170"
                  style={{
                    width: STEP_MARKS[step][0],
                    transition: "width .8s cubic-bezier(.15,.9,.34,.95)",
                  }}
                />
              </clipPath>
            </defs>
            <path
              d={STEP_PATH}
              fill="none"
              stroke="#000"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              clipPath="url(#nm-method-clip)"
            />
          </svg>
          {METHOD.map((x, k) => (
            <button
              key={x.key}
              type="button"
              role="tab"
              id={`method-tab-${x.key}`}
              aria-selected={k === step}
              aria-controls="method-panel"
              tabIndex={k === step ? 0 : -1}
              onClick={() => go(k)}
              data-testid={`method-tab-${x.key}`}
              className="group absolute -translate-x-1/2 -translate-y-1/2 p-3"
              style={{
                left: `${STEP_MARKS[k][0] / 10}%`,
                top: `${(STEP_MARKS[k][1] / 120) * 100}%`,
              }}
            >
              <span
                className={`block rounded-full border border-black transition-all duration-500 ${k === step ? "h-4 w-4 bg-black ring-4 ring-white" : k < step ? "h-2.5 w-2.5 bg-black" : "h-2.5 w-2.5 bg-[#f7f7f7] group-hover:bg-black/30"}`}
              />
              <span
                className={`absolute left-1/2 top-9 whitespace-nowrap text-[11px] font-medium transition-opacity duration-500 ${k === step ? "opacity-100" : "opacity-35 group-hover:opacity-70"} ${k === 0 ? "-translate-x-[20%]" : k === METHOD.length - 1 ? "-translate-x-[80%]" : "-translate-x-1/2"}`}
              >
                {SERVICES[k].title}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
